import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  List,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  alpha,
  useTheme
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  Description as ScriptIcon,
  ImportExport as ImportIcon,
  MovieFilter as BreakdownIcon
} from '@mui/icons-material';
import * as scriptService from '../services/scriptService';
import { getProjectScripts } from '../services/scriptService';
import ScriptManager from './ScriptManager';
// Import TAG_TYPES from SimpleScreenplayViewer for consistency
import { TAG_TYPES } from './SimpleScreenplayViewer';
import { API_URL } from '../config';

// Professional film production breakdown categories and colors
const categoryColors = {
  'CAST': '#E57373',         // Light Red
  'STUNTS': '#FFB74D',       // Light Orange
  'EXTRAS': '#81C784',       // Light Green
  'EXTRAS_SILENT': '#FFF176', // Light Yellow
  'PROPS': '#9575CD',        // Light Purple
  'SET_DRESSING': '#4FC3F7',  // Light Blue
  'SPECIAL_EFFECTS': '#4DB6AC', // Light Teal
  'VEHICLES': '#F06292',     // Light Pink
  'ANIMALS': '#FF8A65',      // Light Deep Orange
  'WARDROBE': '#7986CB',     // Light Indigo
  'MAKEUP': '#FFD54F',       // Light Amber
  'HAIR': '#A1887F',         // Light Brown
  'SOUND': '#90A4AE',        // Light Blue Grey
  'MUSIC': '#7E57C2',        // Light Deep Purple
  'CAMERA': '#4DD0E1',       // Light Cyan
  'LIGHTING': '#DCE775',     // Light Lime
  'GRIP': '#BA68C8',         // Light Purple
  'ELECTRICAL': '#FF8A65',   // Light Deep Orange
  'LOCATIONS': '#4DB6AC',    // Light Teal
  'PRODUCTION_NOTES': '#78909C' // Light Blue Grey
};

const categoryLabels = {
  'CAST': 'Cast',
  'STUNTS': 'Stunts',
  'EXTRAS': 'Extras/Atmosphere',
  'EXTRAS_SILENT': 'Extras/Silent',
  'PROPS': 'Props',
  'SET_DRESSING': 'Set Dressing',
  'SPECIAL_EFFECTS': 'Special Effects',
  'VEHICLES': 'Vehicles',
  'ANIMALS': 'Animals',
  'WARDROBE': 'Wardrobe',
  'MAKEUP': 'Makeup',
  'HAIR': 'Hair',
  'SOUND': 'Sound Effects',
  'MUSIC': 'Music',
  'CAMERA': 'Camera Equipment',
  'LIGHTING': 'Lighting',
  'GRIP': 'Grip Equipment',
  'ELECTRICAL': 'Electrical',
  'LOCATIONS': 'Locations',
  'PRODUCTION_NOTES': 'Production Notes'
};

const Breakdown = ({ projectId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scenes, setScenes] = useState([]);
  const [selectedScene, setSelectedScene] = useState(null);
  const [breakdownItems, setBreakdownItems] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [apiScenes, setApiScenes] = useState([]); // raw scenes from API with stable scene numbers
  const [selectedScript, setSelectedScript] = useState(null);
  const [selectedApiScene, setSelectedApiScene] = useState(null);
  const [scriptManagerOpen, setScriptManagerOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [project, setProject] = useState(null);
  const [sceneNameOverrides, setSceneNameOverrides] = useState({});
  const [sceneDescriptionOverrides, setSceneDescriptionOverrides] = useState({});
  const theme = useTheme();
  
  // Consistent text field styling
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontSize: '0.95rem',
      fontWeight: 600
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.default, 0.3)
        : alpha(theme.palette.grey[50], 0.5),
      borderRadius: 1,
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderWidth: 1.5
      },
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.default, 0.4)
          : alpha(theme.palette.grey[50], 0.8),
        '& fieldset': {
          borderColor: theme.palette.primary.main
        }
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.05)
          : alpha(theme.palette.primary.main, 0.02),
        '& fieldset': {
          borderWidth: 2
        }
      }
    }
  };

  // Fetch scripts when component mounts
  useEffect(() => {
    if (projectId) {
      fetchProject();
      fetchScripts();
    }
  }, [projectId]);

  // Load persisted scene name overrides for this project
  useEffect(() => {
    try {
      const key = `sceneNameOverrides:${projectId}`;
      const raw = localStorage.getItem(key);
      setSceneNameOverrides(raw ? JSON.parse(raw) : {});
    } catch {}
  }, [projectId]);

  // Load persisted scene description overrides for this project
  useEffect(() => {
    try {
      const key = `sceneDescriptionOverrides:${projectId}`;
      const raw = localStorage.getItem(key);
      setSceneDescriptionOverrides(raw ? JSON.parse(raw) : {});
    } catch {}
  }, [projectId]);

  const setSceneNameOverride = (sceneId, name) => {
    setSceneNameOverrides(prev => {
      const next = { ...prev, [sceneId]: name };
      try { localStorage.setItem(`sceneNameOverrides:${projectId}`, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const setSceneDescriptionOverride = (sceneId, desc) => {
    setSceneDescriptionOverrides(prev => {
      const next = { ...prev, [sceneId]: desc };
      try { localStorage.setItem(`sceneDescriptionOverrides:${projectId}`, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  
  // Fetch project details
  const fetchProject = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Fetch project details from the API
      const response = await fetch(`${API_URL}/api/v1/projects/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch project');
      }
      
      const data = await response.json();
      console.log('Project data:', data);
      console.log('Project title:', data.title || data.name || 'Not found');
      console.log('Project structure:', JSON.stringify(data, null, 2));
      setProject(data);
    } catch (err) {
      console.error('Error fetching project:', err);
      setError('Failed to fetch project: ' + err.message);
    }
  };
  
  // Fetch scenes when the selected script changes
  useEffect(() => {
    if (selectedScript) {
      fetchScenes();
    }
  }, [selectedScript]);
  
  // Fetch available scripts
  const fetchScripts = async () => {
    setLoading(true);
    setError('');
    
    try {
      const scriptsData = await getProjectScripts(projectId);
      setScripts(scriptsData);
      
      // If there are scripts and none is selected, select the first one
      if (scriptsData.length > 0 && !selectedScript) {
        setSelectedScript(scriptsData[0]);
      }
    } catch (err) {
      console.error('Error fetching scripts:', err);
      setError('Failed to fetch scripts: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Keep selectedApiScene in sync with selection and available API scenes
  useEffect(() => {
    if (!selectedScene) return;
    try {
      const match = resolveApiScene(selectedScene);
      setSelectedApiScene(match || null);
      try { console.log('[Breakdown] Selection changed -> API match:', { selectedScene, match }); } catch {}
    } catch {}
  }, [selectedScene, apiScenes]);

  // Fetch scenes from the script
  const fetchScenes = async () => {
    if (!selectedScript) return;
    
    setLoading(true);
    setError('');
    
    try {
      // First, get the script content to extract proper sluglines if needed
      let scriptContent = null;
      try {
        const scriptData = await scriptService.getScriptContent(selectedScript._id, 'latest', projectId);
        scriptContent = scriptData.content;
        console.log('Got script content, length:', scriptContent ? scriptContent.length : 0);
      } catch (err) {
        console.warn('Could not get script content for slugline extraction:', err);
      }
      
      // Now fetch the scenes
      const response = await fetch(`${API_URL}/api/v1/scripts/project/${projectId}/scenes`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch scenes: ${response.status}`);
      }
      
      const data = await response.json();
      
      console.log('Raw scenes from API:', data.scenes);
      const apiScenesLocal = Array.isArray(data.scenes) ? data.scenes : [];
      setApiScenes(apiScenesLocal);
      
      // If we have script content, try to enhance scene data with proper sluglines
      if (scriptContent && data.scenes && data.scenes.length > 0) {
        const scriptLines = scriptContent.split('\n');
        
        // Try to enhance scene data with better sluglines
        data.scenes.forEach(scene => {
          // If scene has line numbers but no heading, try to extract it
          if ((scene.startLine !== undefined) && (!scene.heading || scene.heading === 'Untitled Scene')) {
            const startLine = parseInt(scene.startLine);
            if (!isNaN(startLine) && startLine >= 0 && startLine < scriptLines.length) {
              // Look for a slugline near this position
              for (let i = Math.max(0, startLine - 2); i <= Math.min(scriptLines.length - 1, startLine + 2); i++) {
                const line = scriptLines[i].trim();
                if (line && (line.includes('INT.') || line.includes('EXT.'))) {
                  scene.heading = line;
                  console.log(`Enhanced scene ${scene.id || scene._id} with slugline: ${line}`);
                  break;
                }
              }
            }
          }
          
          // If scene still has no heading but has content, try to extract from content
          if ((!scene.heading || scene.heading === 'Untitled Scene') && scene.content) {
            const contentLines = scene.content.split('\n');
            for (let i = 0; i < Math.min(contentLines.length, 5); i++) {
              const line = contentLines[i].trim();
              if (line && (line.includes('INT.') || line.includes('EXT.'))) {
                scene.heading = line;
                console.log(`Extracted slugline from content: ${line}`);
                break;
              }
            }
          }
        });
      }
      
      // Get scenes directly from the script content to ensure screenplay order
      let scriptScenes = [];
      
      if (scriptContent) {
        console.log('Extracting scenes directly from script content to preserve screenplay order');
        const scriptLines = scriptContent.split('\n');
        let currentSceneStart = -1;
        
        // Find all scene headings in the script
        for (let i = 0; i < scriptLines.length; i++) {
          const line = scriptLines[i].trim();
          // Look for standard screenplay sluglines
          if (line && (line.match(/^(INT\.|EXT\.|INT\/EXT\.|EXT\/INT\.|INT |EXT |I\/E )/) || 
                      line.match(/^MONTAGE/i) || // Include MONTAGE as a scene header
                      (line === line.toUpperCase() && 
                       (line.includes('INT.') || line.includes('EXT.') || 
                        line.includes('INT ') || line.includes('EXT ') ||
                        line.includes('MONTAGE')))))  { // Also check for MONTAGE in all caps lines
            
            // If we already found a scene, add it to our list
            if (currentSceneStart !== -1) {
              const sceneText = scriptLines.slice(currentSceneStart, i).join('\n');
              scriptScenes.push({
                startLine: currentSceneStart,
                endLine: i - 1,
                heading: scriptLines[currentSceneStart],
                content: sceneText,
                // Generate a unique ID for this scene
                id: `script_scene_${currentSceneStart}`
              });
            }
            
            // Start a new scene
            currentSceneStart = i;
          }
        }
        
        // Add the last scene if there is one
        if (currentSceneStart !== -1) {
          const sceneText = scriptLines.slice(currentSceneStart).join('\n');
          scriptScenes.push({
            startLine: currentSceneStart,
            endLine: scriptLines.length - 1,
            heading: scriptLines[currentSceneStart],
            content: sceneText,
            id: `script_scene_${currentSceneStart}`
          });
        }
        
        console.log('Extracted scenes directly from script:', scriptScenes.length);
      }
      
      // Helper to resolve API scene for a given scene
      const norm = (s) => String(s || '').toUpperCase().replace(/^SC(?:ENE)?\.?\s*/,'').replace(/[^A-Z0-9]/g,'').replace(/^0+(?=\d)/,'');
      const resolveApiFor = (scene) => {
        if (!scene) return null;
        const sid = scene.id || scene._id;
        if (sid) {
          const m = (apiScenesLocal || []).find(s => String(s.id || s._id) === String(sid));
          if (m) return m;
        }
        const sStart = parseInt(scene.startLine);
        if (!isNaN(sStart)) {
          const byRange = (apiScenesLocal || []).find(s => {
            const a = parseInt(s.startLine);
            const b = parseInt(s.endLine);
            return !isNaN(a) && !isNaN(b) && sStart >= a && sStart <= b;
          });
          if (byRange) return byRange;
        }
        const head = (scene.heading || '').trim();
        if (head) {
          const byHead = (apiScenesLocal || []).find(s => (s.heading || '').trim() === head);
          if (byHead) return byHead;
        }
        const sceneNum = typeof scene.sceneNumber === 'object' ? (scene.sceneNumber?.standard || scene.sceneNumber?.original) : (scene.sceneNumber || scene.number);
        const key = norm(sceneNum);
        if (key) {
          const byNum = (apiScenesLocal || []).find(s => {
            const cand = typeof s.sceneNumber === 'object' ? (s.sceneNumber?.standard || s.sceneNumber?.original) : (s.sceneNumber || s.number);
            return norm(cand) === key;
          });
          if (byNum) return byNum;
        }
        return null;
      };

      // If we successfully extracted scenes from the script, use those
      // Otherwise, fall back to the scenes from the API
      let sortedScenes;
      
      if (scriptScenes.length > 0) {
        // Use scenes extracted directly from the script (already in screenplay order)
        sortedScenes = scriptScenes;
        console.log('Using scenes extracted directly from screenplay');
      } else {
        // Fall back to sorting the API scenes
        console.log('Falling back to API scenes with sorting');
        sortedScenes = [...(data.scenes || [])].sort((a, b) => {
          // First try to sort by startLine if available (most reliable for screenplay order)
          if (a.startLine !== undefined && b.startLine !== undefined) {
            return parseInt(a.startLine) - parseInt(b.startLine);
          }
          
          // Try to sort by sceneNumber if it's a simple number
          if (typeof a.sceneNumber === 'number' && typeof b.sceneNumber === 'number') {
            return a.sceneNumber - b.sceneNumber;
          }
          
          // Handle string scene numbers (e.g., "1A", "1B")
          if (typeof a.sceneNumber === 'string' && typeof b.sceneNumber === 'string') {
            // Extract numeric parts for comparison
            const aMatch = a.sceneNumber.match(/^(\d+)/);
            const bMatch = b.sceneNumber.match(/^(\d+)/);
            
            if (aMatch && bMatch) {
              const aNum = parseInt(aMatch[1]);
              const bNum = parseInt(bMatch[1]);
              
              if (aNum !== bNum) {
                return aNum - bNum;
              } else {
                // If numeric parts are equal, sort alphabetically
                return a.sceneNumber.localeCompare(b.sceneNumber);
              }
            }
          }
          
          // Try to sort by sceneNumber.standard if available
          if (a.sceneNumber?.standard && b.sceneNumber?.standard) {
            // Handle potential non-numeric values
            const aStandard = a.sceneNumber.standard.toString();
            const bStandard = b.sceneNumber.standard.toString();
            
            // Extract numeric parts
            const aMatch = aStandard.match(/^(\d+)/);
            const bMatch = bStandard.match(/^(\d+)/);
            
            if (aMatch && bMatch) {
              const aNum = parseInt(aMatch[1]);
              const bNum = parseInt(bMatch[1]);
              
              if (aNum !== bNum) {
                return aNum - bNum;
              } else {
                // If numeric parts are equal, sort alphabetically
                return aStandard.localeCompare(bStandard);
              }
            }
          }
          
          // Only use order property as a last resort (this is the oneliner order)
          if (a.order !== undefined && b.order !== undefined) {
            return parseInt(a.order) - parseInt(b.order);
          }
          
          // Fall back to displayIndex
          if (a.displayIndex !== undefined && b.displayIndex !== undefined) {
            return parseInt(a.displayIndex) - parseInt(b.displayIndex);
          }
          
          // Last resort - sort by heading text if available
          if (a.heading && b.heading) {
            return a.heading.localeCompare(b.heading);
          }
          
          // Absolute last resort - use array position
          return 0;
        });
      }
      
      console.log('Sorted scenes:', sortedScenes.map(s => {
        const sceneNum = typeof s.sceneNumber === 'object' ? 
          (s.sceneNumber.standard || s.sceneNumber.scriptOrder) : s.sceneNumber;
        return { id: s.id, number: sceneNum, startLine: s.startLine, heading: s.heading?.substring(0, 20) };
      }));
      
      // Enrich each scene with canonical scene number from API match
      const enriched = (sortedScenes || []).map(sc => {
        const apiMatch = resolveApiFor(sc);
        let resolvedNumber = null;
        if (apiMatch) {
          resolvedNumber = (apiMatch.sceneNumber && typeof apiMatch.sceneNumber === 'object')
            ? (apiMatch.sceneNumber.standard || apiMatch.sceneNumber.original || apiMatch.number)
            : (apiMatch.sceneNumber || apiMatch.number);
        }
        return { ...sc, sceneNumberResolved: resolvedNumber, linkedApiId: apiMatch ? (apiMatch.id || apiMatch._id) : null };
      });

      setScenes(enriched);
      
      // If we have scenes, fetch tags for the first scene
      if (enriched.length > 0) {
        fetchTagsForScene(enriched[0]);
      }
    } catch (err) {
      console.error('Error fetching scenes:', err);
      setError('Failed to fetch scenes: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Auto-tag the script to generate breakdown items
  const autoTagScript = async () => {
    if (!selectedScript) {
      setNotification({
        open: true,
        message: 'No script selected',
        severity: 'error'
      });
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const result = await scriptService.autoTagScript(selectedScript._id, projectId);
      
      setNotification({
        open: true,
        message: `Script auto-tagged successfully with ${result.stats.totalTags} items`,
        severity: 'success'
      });
      
      // Refresh the breakdown items
      fetchScenes();
    } catch (err) {
      console.error('Error auto-tagging script:', err);
      setError('Failed to auto-tag script: ' + err.message);
      setNotification({
        open: true,
        message: 'Failed to auto-tag script',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle script selection
  const handleScriptChange = (event) => {
    const scriptId = event.target.value;
    const script = scripts.find(s => s._id === scriptId);
    setSelectedScript(script);
  };
  
  // Open script manager
  const handleOpenScriptManager = () => {
    setScriptManagerOpen(true);
  };
  
  // Handle script selection from script manager
  const handleScriptSelected = (script) => {
    setSelectedScript(script);
    setScriptManagerOpen(false);
  };
  
  // Import scenes from ScriptManager
  const handleImportFromScriptManager = () => {
    if (!selectedScript) {
      setNotification({
        open: true,
        message: 'Please select a script first',
        severity: 'warning'
      });
      return;
    }
    
    setImportDialogOpen(true);
  };
  
  // Handle import confirmation
  const handleImportConfirm = async () => {
    setLoading(true);
    
    try {
      // This will trigger the fetchScenes function which already gets scenes from the script
      await fetchScenes();
      
      setNotification({
        open: true,
        message: 'Scenes imported successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error importing scenes:', err);
      setNotification({
        open: true,
        message: 'Failed to import scenes',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setImportDialogOpen(false);
    }
  };

  // Fetch tags for a specific scene
  const fetchTagsForScene = async (scene) => {
    if (!selectedScript || !scene) return;
    
    console.log('Fetching tags for scene:', scene);
    // Note: setSelectedScene is now handled in handleSceneSelect
    setLoading(true);
    setBreakdownItems({}); // Clear previous items immediately

    try {
      // Get script content and tags
      const scriptData = await scriptService.getScriptContent(selectedScript._id, 'latest', projectId);

      if (!scriptData.tags || scriptData.tags.length === 0) {
        console.log('No tags found in script');
        setBreakdownItems({});
        setLoading(false);
        return;
      }
      
      console.log('Total script tags:', scriptData.tags.length);
      console.log('Script content length:', scriptData.content ? scriptData.content.length : 'N/A');
      
      // Note: Both tags and scenes are now stored relative to bodyContent (cover already stripped),
      // so we do NOT need to subtract coverOffset. They share the same coordinate space.

      // Determine scene boundaries
      let sceneStart, sceneEnd;

      // If scene has specific line numbers, use those
      if (scene.startLine !== undefined && scene.endLine !== undefined) {
        sceneStart = parseInt(scene.startLine);
        sceneEnd = parseInt(scene.endLine);
        console.log('Using explicit line numbers:', { sceneStart, sceneEnd });
      } 
      // If scene has a range property
      else if (scene.range) {
        sceneStart = parseInt(scene.range.start) || 0;
        sceneEnd = parseInt(scene.range.end) || Number.MAX_SAFE_INTEGER;
        console.log('Using range property:', { sceneStart, sceneEnd });
      }
      // If scene has text content, try to find its position in the script
      else if ((scene.text && typeof scene.text === 'string') || (scene.heading && typeof scene.heading === 'string')) {
        const scriptLines = scriptData.content ? scriptData.content.split('\n') : [];
        const searchText = scene.heading || scene.text;
        
        console.log('Searching for scene text in script:', searchText);
        
        // Try to find an exact match first
        let sceneIndex = -1;
        if (scriptLines.length > 0 && searchText) {
          sceneIndex = scriptLines.findIndex(line => line.includes(searchText));
        }
        
        // If exact match fails, try to find a partial match
        if (sceneIndex === -1 && searchText && searchText.length > 10) {
          const partialText = searchText.substring(0, 10);
          console.log('Trying partial match with:', partialText);
          sceneIndex = scriptLines.findIndex(line => line && line.includes(partialText));
        }
        
        if (sceneIndex !== -1) {
          // Look for the next scene header to determine end
          sceneStart = sceneIndex;
          
          // Find the next scene header (usually starts with INT. or EXT.)
          const nextSceneIndex = scriptLines.findIndex((line, index) => 
            index > sceneIndex && line && /^(INT\.|EXT\.|INT |EXT )/i.test(line.trim())
          );
          
          sceneEnd = nextSceneIndex !== -1 ? nextSceneIndex - 1 : scriptLines.length - 1;
          console.log('Found scene in content at index:', sceneIndex, 'to', sceneEnd);
        } else {
          // Try using scene ID to match against tags directly
          console.log('Could not find scene text in script content, trying scene ID match');
          if (scene.id) {
            // Look for tags that might have this scene ID
            const matchingTags = scriptData.tags.filter(tag => tag.sceneId === scene.id);
            if (matchingTags.length > 0) {
              // Use the line numbers from these tags
              const tagLineNumbers = matchingTags.map(tag => parseInt(tag.lineNumber)).filter(ln => !isNaN(ln));
              if (tagLineNumbers.length > 0) {
                sceneStart = Math.min(...tagLineNumbers);
                sceneEnd = Math.max(...tagLineNumbers);
                console.log('Using tag line numbers for scene ID match:', { sceneStart, sceneEnd });
              }
            }
          }
          
          // If still no match, use a default range
          if (sceneStart === undefined || sceneEnd === undefined) {
            console.log('No matching method worked, using default boundaries');
            // Try to use index-based boundaries
            const sceneIndex = scenes.findIndex(s => s.id === scene.id);
            if (sceneIndex !== -1) {
              // Estimate line range based on scene position
              const totalLines = scriptData.content ? scriptData.content.split('\n').length : 0;
              const scenesCount = scenes.length;
              const avgLinesPerScene = totalLines / (scenesCount || 1);
              
              sceneStart = Math.floor(sceneIndex * avgLinesPerScene);
              sceneEnd = Math.floor((sceneIndex + 1) * avgLinesPerScene) - 1;
              console.log('Using estimated line range based on scene position:', { sceneStart, sceneEnd });
            } else {
              // Absolute last resort
              sceneStart = 0;
              sceneEnd = Number.MAX_SAFE_INTEGER;
              console.log('Using full script range as last resort');
            }
          }
        }
      }
      // Default fallback
      else {
        console.log('Using default scene boundaries');
        sceneStart = 0;
        sceneEnd = Number.MAX_SAFE_INTEGER;
      }
      
      console.log('Final scene boundaries:', { sceneStart, sceneEnd });
      
      // Debug: Log all tags with their line numbers
      console.log('All tags with line numbers:', scriptData.tags.map(t => ({
        text: t.text,
        lineNumber: t.lineNumber,
        type: t.type
      })));
      
      // Filter tags for the selected scene
      // Both tags and scenes use the same coordinate space (body content, 0-indexed)
      const sceneTags = scriptData.tags.filter(tag => {
        // Ensure lineNumber is treated as a number
        const lineNumber = parseInt(tag.lineNumber);
        const inRange = !isNaN(lineNumber) && lineNumber >= sceneStart && lineNumber <= sceneEnd;

        // If tag has a sceneId, also check if it matches the current scene
        const sceneIdMatch = tag.sceneId && scene.id && tag.sceneId === scene.id;
        
        console.log('Tag filter check:', { 
          text: tag.text, 
          lineNumber, 
          sceneStart, 
          sceneEnd, 
          inRange, 
          sceneIdMatch 
        });

        return inRange || sceneIdMatch;
      });
      
      console.log('Filtered tags for scene:', sceneTags.length);
      
      // Group tags by category
      const groupedTags = sceneTags.reduce((acc, tag) => {
        // Use a default category if type is missing or not in TAG_TYPES
        let category = tag.type || 'PROPS';
        
        // Map CAST to CHARACTER since they've been combined
        if (category === 'CAST') {
          console.log('Mapping CAST tag to CHARACTER tag:', tag.text);
          category = 'CHARACTER';
          tag.type = 'CHARACTER'; // Update the tag's type as well
        }
        
        // Validate that the category exists in TAG_TYPES
        if (!TAG_TYPES[category]) {
          console.warn(`Tag type '${category}' not found in TAG_TYPES, defaulting to PROPS`);
          category = 'PROPS';
        }
        
        if (!acc[category]) {
          acc[category] = [];
        }
        
        // Add the tag with proper color from TAG_TYPES
        const tagWithColor = {
          ...tag,
          color: tag.color || (TAG_TYPES[category] ? TAG_TYPES[category].color : categoryColors[category] || '#9575CD')
        };
        
        acc[category].push(tagWithColor);
        return acc;
      }, {});
      
      console.log('Grouped tags by category:', Object.keys(groupedTags));
      
      // Force a state update with the new breakdown items
      setBreakdownItems({...groupedTags});
    } catch (err) {
      console.error('Error fetching tags for scene:', err);
      setError('Failed to fetch tags for scene: ' + err.message);
      setBreakdownItems({});
    } finally {
      setLoading(false);
    }
  };

  // Resolve API scene that corresponds to a given scene entry (script-extracted or API)
  const resolveApiScene = (scene) => {
    if (!scene) return null;
    // 1) Direct id match
    const sid = scene.id || scene._id;
    if (sid) {
      const m = (apiScenes || []).find(s => String(s.id || s._id) === String(sid));
      if (m) return m;
    }
    // 2) Range overlap by startLine
    const sStart = parseInt(scene.startLine);
    if (!isNaN(sStart)) {
      const byRange = (apiScenes || []).find(s => {
        const a = parseInt(s.startLine);
        const b = parseInt(s.endLine);
        return !isNaN(a) && !isNaN(b) && sStart >= a && sStart <= b;
      });
      if (byRange) return byRange;
    }
    // 3) Exact heading match
    const head = (scene.heading || '').trim();
    if (head) {
      const byHead = (apiScenes || []).find(s => (s.heading || '').trim() === head);
      if (byHead) return byHead;
    }
    // 4) Fallback: match by normalized scene number if present
    const sceneNum = typeof scene.sceneNumber === 'object' ? (scene.sceneNumber?.standard || scene.sceneNumber?.original) : (scene.sceneNumber || scene.number);
    const norm = (s) => String(s || '').toUpperCase().replace(/^SC(?:ENE)?\.?\s*/,'').replace(/[^A-Z0-9]/g,'').replace(/^0+(?=\d)/,'');
    const key = norm(sceneNum);
    if (key) {
      const byNum = (apiScenes || []).find(s => {
        const cand = typeof s.sceneNumber === 'object' ? (s.sceneNumber?.standard || s.sceneNumber?.original) : (s.sceneNumber || s.number);
        return norm(cand) === key;
      });
      if (byNum) return byNum;
    }
    return null;
  };

  // Handle scene selection
  const handleSceneSelect = (scene) => {
    console.log('Scene selected:', scene);
    setSelectedScene(scene);
    try { setSelectedApiScene(resolveApiScene(scene)); } catch {}
    fetchTagsForScene(scene);
  };

  // Handle adding a new breakdown item
  const handleAddItem = () => {
    // Use the first TAG_TYPE as default, or fall back to categoryLabels if needed
    const defaultType = Object.keys(TAG_TYPES)[0] || Object.keys(categoryLabels)[0];
    const defaultColor = TAG_TYPES[defaultType]?.color || Object.values(categoryColors)[0];
    
    // Extract scene number from selected scene to maintain association after reordering
    const sceneNumber = selectedScene ? 
      (typeof selectedScene.sceneNumber === 'object' ? 
        (selectedScene.sceneNumber.standard || selectedScene.sceneNumber.scriptOrder) : 
        selectedScene.sceneNumber) : null;
    
    setEditItem({
      id: `manual_${Date.now()}`,
      type: defaultType,
      text: '',
      lineNumber: selectedScene ? selectedScene.startLine : 0,
      color: defaultColor,
      keyword: 'Manual Entry',
      confidence: 1.0,
      // CRITICAL: Store scene identifiers so tags stay with correct scene after reordering
      sceneId: selectedScene ? (selectedScene.id || selectedScene._id) : null,
      sceneNumber: sceneNumber,
      number: sceneNumber
    });
    setEditDialogOpen(true);
  };

  // Handle editing an existing breakdown item
  const handleEditItem = (item) => {
    setEditItem({...item});
    setEditDialogOpen(true);
  };

  // Handle saving a breakdown item
  const handleSaveItem = async () => {
    if (!selectedScript || !editItem) return;
    
    setLoading(true);
    
    try {
      // Get current tags
      const scriptData = await scriptService.getScriptContent(selectedScript._id, 'latest', projectId);
      let currentTags = scriptData.tags || [];
      
      // Remove the old item if it exists
      currentTags = currentTags.filter(tag => tag.id !== editItem.id);
      
      // Add the updated/new item
      currentTags.push(editItem);
      
      // Save the updated tags
      await scriptService.updateScriptTags(selectedScript._id, projectId, currentTags);
      
      setNotification({
        open: true,
        message: 'Breakdown item saved successfully',
        severity: 'success'
      });
      
      // Refresh the breakdown items
      fetchTagsForScene(selectedScene);
    } catch (err) {
      console.error('Error saving breakdown item:', err);
      setNotification({
        open: true,
        message: 'Failed to save breakdown item',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setEditDialogOpen(false);
    }
  };

  // Handle deleting a breakdown item
  const handleDeleteItem = async (item) => {
    if (!selectedScript || !item) return;
    
    setLoading(true);
    
    try {
      // Get current tags
      const scriptData = await scriptService.getScriptContent(selectedScript._id, 'latest', projectId);
      let currentTags = scriptData.tags || [];
      
      // Remove the item
      currentTags = currentTags.filter(tag => tag.id !== item.id);
      
      // Save the updated tags
      await scriptService.updateScriptTags(selectedScript._id, projectId, currentTags);
      
      setNotification({
        open: true,
        message: 'Breakdown item deleted successfully',
        severity: 'success'
      });
      
      // Refresh the breakdown items
      fetchTagsForScene(selectedScene);
    } catch (err) {
      console.error('Error deleting breakdown item:', err);
      setNotification({
        open: true,
        message: 'Failed to delete breakdown item',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle printing the breakdown sheet
  const handlePrint = () => {
    if (!selectedScene) {
      setNotification({
        open: true,
        message: 'Please select a scene first',
        severity: 'warning'
      });
      return;
    }
    
    // Get scene information (prefer API scene match for canonical number)
    let sceneNumber = null;
    const apiMatch = selectedApiScene || resolveApiScene(selectedScene);
    if (apiMatch) {
      sceneNumber = (apiMatch.sceneNumber && typeof apiMatch.sceneNumber === 'object')
        ? (apiMatch.sceneNumber.standard || apiMatch.sceneNumber.original || apiMatch.number)
        : (apiMatch.sceneNumber || apiMatch.number);
    }
    if (!sceneNumber && selectedScene) {
      // Prefer enriched canonical number if available
      if (!sceneNumber && selectedScene.sceneNumberResolved) {
        sceneNumber = selectedScene.sceneNumberResolved;
      }
      if (typeof selectedScene.sceneNumber === 'object' && selectedScene.sceneNumber) {
        sceneNumber = selectedScene.sceneNumber.standard || selectedScene.sceneNumber.original || null;
      } else {
        sceneNumber = selectedScene.number || selectedScene.sceneNumber || null;
      }
    }
    if (!sceneNumber && selectedScene) {
      // Final fallback: derive from the scene object within the scenes list
      try {
        const idx = scenes.findIndex(s => (s.id || s._id) === (selectedScene.id || selectedScene._id));
        if (idx !== -1) {
          const s = scenes[idx];
          sceneNumber = s.sceneNumberResolved
            || (typeof s.sceneNumber === 'object' ? (s.sceneNumber?.standard || s.sceneNumber?.original) : (s.sceneNumber || s.number))
            || String(idx + 1);
        }
      } catch {}
    }
    if (!sceneNumber) sceneNumber = '1';
    sceneNumber = String(sceneNumber);
    // Debug: verify resolved values prior to rendering print HTML
    try { console.log('[Breakdown Print] Using sceneNumber:', sceneNumber, { selectedScene, selectedApiScene }); } catch {}

    // Prefer API scene's heading and attributes for display
    const sceneNameBase = (apiMatch && apiMatch.heading) ? apiMatch.heading : (
      selectedScene.heading || (typeof selectedScene.text === 'string' ? selectedScene.text : '')
    );
    const overrideName = sceneNameOverrides[(selectedScene.id || selectedScene._id) || ''] || '';
    // Per request: leave Scene Name blank until user inputs one
    const sceneName = overrideName || '';
    // Description override (blank unless provided)
    const overrideDescription = sceneDescriptionOverrides[(selectedScene.id || selectedScene._id) || ''] || '';
    const description = overrideDescription || '';
    const intExtBase = (apiMatch && (apiMatch.intExt || apiMatch.location_type))
      ? (apiMatch.intExt || apiMatch.location_type)
      : (selectedScene.intExt || selectedScene.location || '');
    const dayNightBase = (apiMatch && (apiMatch.effectiveDayNight || apiMatch.time))
      ? (apiMatch.effectiveDayNight || apiMatch.time)
      : (selectedScene.timeOfDay || selectedScene.time || '');

    // Parse slugline to extract INT/EXT, location, and time-of-day
    const parseSlugline = (slug) => {
      if (!slug || typeof slug !== 'string') return { intExt: '', location: '', time: '' };
      const s = slug.trim();
      // Common forms: "INT. LOCATION - DAY", "EXT. FARMHOUSE - NIGHT", "INT/EXT. CAR - EVENING"
      const m = s.match(/^(INT\.?\/EXT\.?|EXT\.?\/INT\.?|INT\.?|EXT\.?)\s+(.+?)(?:\s*-\s*(DAY|NIGHT|EVENING|MORNING|AFTERNOON|DUSK|DAWN))?\s*$/i);
      if (m) {
        const intExt = m[1].toUpperCase().replace(/\s+/g, '');
        const location = m[2].trim();
        const time = (m[3] || '').toUpperCase();
        return { intExt, location, time };
      }
      // Fallback: try split by dash
      const parts = s.split('-');
      const left = parts[0] || '';
      const right = (parts[1] || '').trim().toUpperCase();
      let intExt = '';
      if (/INT/i.test(left) && /EXT/i.test(left)) intExt = 'INT/EXT.';
      else if (/INT/i.test(left)) intExt = 'INT.';
      else if (/EXT/i.test(left)) intExt = 'EXT.';
      const location = left.replace(/INT\.?|EXT\.?|INT\/EXT\.?/gi, '').trim();
      const time = /DAY|NIGHT|EVENING|MORNING|AFTERNOON|DUSK|DAWN/.test(right) ? right : '';
      return { intExt, location, time };
    };
    const slugSrc = (apiMatch && apiMatch.heading) ? apiMatch.heading : (selectedScene.heading || selectedScene.text || '');
    const parsedSlug = parseSlugline(slugSrc);
    const intExt = parsedSlug.intExt || intExtBase;
    const dayNight = parsedSlug.time || dayNightBase;
    const locationFromSlug = parsedSlug.location || selectedScene.setting || '';
    
    // Remove any previous print iframe to avoid stale content
    try {
      const prev = document.getElementById('breakdown-print-frame');
      if (prev && prev.parentNode) prev.parentNode.removeChild(prev);
    } catch {}

    // Create a hidden iframe for printing
    const iframe = document.createElement('iframe');
    iframe.id = 'breakdown-print-frame';
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    
    // Get project information from the project data
    console.log('Print function - Project data:', project);
    // The project data structure uses projectName, not title
    const projectName = project ? project.projectName : (selectedScript ? selectedScript.title : 'Unknown');
    console.log('Using project name:', projectName);
    const prodCompany = project && project.company ? project.company : 'SCAD Productions';
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    
    // Page label (supports multi-page printing later)
    const currentPage = 1;
    const totalPages = 1;
    const pageLabel = `Page ${currentPage} of ${totalPages}`;
    
    // Generate HTML content for the breakdown sheet
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Breakdown Sheet - Scene ${sceneNumber}</title>
        <style>
          @page { size: letter; margin: 0.1in; }
          html, body { height: 100%; margin: 0; padding: 0; }
          body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, "Noto Sans", "Helvetica Neue", sans-serif; color: #1f2937; }
          .sheet-container { max-width: 8.25in; margin: 0; padding: 0; }
          table { width: 100%; border-collapse: collapse; margin: 0 0 2px 0; page-break-inside: avoid; }
          .sheet-container > table:first-of-type { margin-top: 0; }
          td { border: 1px solid #cfd4da; padding: 3px 4px; text-align: left; vertical-align: top; }
          .header-cell { font-weight: 700; letter-spacing: .02em; color: #111827; }
          .muted { color: #6b7280; }
          .breakdown-cell { height: 95px; vertical-align: top; padding-top: 6px; }
          .category-label { font-weight: 600; display: block; margin-bottom: 2px; text-transform: uppercase; letter-spacing: .03em; color: #111827; }
          .category-color { font-weight: 500; font-size: 0.75em; color: #6b7280; }
          .row-bg td { background: #f6f7f9; }
          .small { font-size: 0.82em; }
        </style>
      </head>
      <body>
        <div class="sheet-container">
          <!-- Header Row 1 -->
          <table class="row-bg">
            <tr>
              <td style="width: 50%"><span class="header-cell">BREAKDOWN SHEET:</span> #${sceneNumber}</td>
              <td style="width: 25%"><span class="header-cell">${pageLabel}</span></td>
              <td style="width: 25%"><span class="header-cell">Date:</span> ${formattedDate}</td>
            </tr>
          </table>
          
          <!-- Header Row 3 -->
          <table class="row-bg">
            <tr>
              <td><span class="header-cell">Project Title:</span> ${projectName}</td>
            </tr>
          </table>
          
          <!-- Scene Info Row 1 -->
          <table>
            <tr>
              <td style="width: 25%"><span class="header-cell">Scene #:</span> ${sceneNumber}</td>
              <td style="width: 50%"><span class="header-cell">Scene Name:</span> ${sceneName}</td>
              <td style="width: 12.5%"><span class="header-cell">INT/EXT:</span> ${intExt}</td>
              <td style="width: 12.5%"><span class="header-cell">D/N:</span> ${dayNight}</td>
            </tr>
          </table>
          
          <!-- Scene Info Row 2 -->
          <table>
            <tr>
              <td style="width: 25%"><span class="header-cell">Script Page:</span> ${selectedScene.startLine || ''}</td>
              <td style="width: 75%"><span class="header-cell">Location Name:</span> ${locationFromSlug}</td>
            </tr>
          </table>
          
          <!-- Description -->
          <table>
            <tr>
              <td><span class="header-cell">Description:</span> ${description}</td>
            </tr>
          </table>
      
          <!-- Character/Cast Row -->
          <table>
            <tr>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">CHARACTER <span class="category-color">(red)</span></span>
                ${breakdownItems['CHARACTER'] ? breakdownItems['CHARACTER'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">STUNTS <span class="category-color">(orange)</span></span>
                ${breakdownItems['STUNTS'] ? breakdownItems['STUNTS'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">EXTRAS/ATMOSPHERE <span class="category-color">(green)</span></span>
                ${breakdownItems['EXTRAS'] ? breakdownItems['EXTRAS'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
            </tr>
          </table>
          
          <!-- Extras/Silent Row -->
          <table>
            <tr>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">EXTRAS/SILENT <span class="category-color">(yellow)</span></span>
                ${breakdownItems['EXTRAS_SILENT'] ? breakdownItems['EXTRAS_SILENT'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">SET DRESSING <span class="category-color">(light blue)</span></span>
                ${breakdownItems['SET_DRESSING'] ? breakdownItems['SET_DRESSING'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">LOCATIONS <span class="category-color">(teal)</span></span>
                ${breakdownItems['LOCATIONS'] ? breakdownItems['LOCATIONS'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
            </tr>
          </table>
          
          <!-- Special Effects/Props/Vehicles Row -->
          <table>
            <tr>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">SPECIAL EFFECTS <span class="category-color">(teal)</span></span>
                ${breakdownItems['SPECIAL_EFFECTS'] ? breakdownItems['SPECIAL_EFFECTS'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">PROPS <span class="category-color">(purple)</span></span>
                ${breakdownItems['PROPS'] ? breakdownItems['PROPS'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">VEHICLES <span class="category-color">(pink)</span></span>
                ${breakdownItems['VEHICLES'] ? breakdownItems['VEHICLES'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
            </tr>
          </table>
          
          <!-- Animals/Wardrobe/Makeup Row -->
          <table>
            <tr>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">ANIMALS <span class="category-color">(deep orange)</span></span>
                ${breakdownItems['ANIMALS'] ? breakdownItems['ANIMALS'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">WARDROBE <span class="category-color">(indigo)</span></span>
                ${breakdownItems['WARDROBE'] ? breakdownItems['WARDROBE'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">MAKEUP <span class="category-color">(amber)</span></span>
                ${breakdownItems['MAKEUP'] ? breakdownItems['MAKEUP'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
            </tr>
          </table>
          
          <!-- Hair/Sound/Music Row -->
          <table>
            <tr>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">HAIR <span class="category-color">(brown)</span></span>
                ${breakdownItems['HAIR'] ? breakdownItems['HAIR'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">SOUND EFFECTS <span class="category-color">(blue grey)</span></span>
                ${breakdownItems['SOUND'] ? breakdownItems['SOUND'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">MUSIC <span class="category-color">(deep purple)</span></span>
                ${breakdownItems['MUSIC'] ? breakdownItems['MUSIC'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
            </tr>
          </table>
          
          <!-- Camera/Lighting/Grip Row -->
          <table>
            <tr>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">CAMERA EQUIPMENT <span class="category-color">(cyan)</span></span>
                ${breakdownItems['CAMERA'] ? breakdownItems['CAMERA'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">LIGHTING <span class="category-color">(lime)</span></span>
                ${breakdownItems['LIGHTING'] ? breakdownItems['LIGHTING'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">GRIP EQUIPMENT <span class="category-color">(purple)</span></span>
                ${breakdownItems['GRIP'] ? breakdownItems['GRIP'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
            </tr>
          </table>
          
          <!-- Electrical/Production Notes Row -->
          <table>
            <tr>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">ELECTRICAL <span class="category-color">(deep orange)</span></span>
                ${breakdownItems['ELECTRICAL'] ? breakdownItems['ELECTRICAL'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
              <td style="width: 33%" class="breakdown-cell">
                <span class="category-label">PRODUCTION NOTES <span class="category-color">(blue grey)</span></span>
                ${breakdownItems['PRODUCTION_NOTES'] ? breakdownItems['PRODUCTION_NOTES'].map(item => `<div>${item.text}</div>`).join('') : ''}
              </td>
              <td style="width: 33%"></td>
            </tr>
          </table>
          
          
        </div>
      </body>
    </html>
  `;
  
  // Set onload before assigning content, then use srcdoc for reliability
  iframe.onload = () => {
    setTimeout(() => {
      try { iframe.contentWindow.focus(); } catch {}
      try { iframe.contentWindow.print(); } catch {}
      // Remove the iframe after printing
      setTimeout(() => {
        try { if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe); } catch {}
      }, 150);
    }, 200);
  };
  try {
    iframe.srcdoc = printContent;
  } catch {
    // Fallback to document.write if srcdoc unsupported
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(printContent);
    doc.close();
  }
};

return (
  <Paper elevation={0} sx={{ 
    p: 4, 
    borderRadius: 1,
    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
    backgroundColor: (theme) => theme.palette.background.paper
  }}>
    {/* Header with title and action buttons */}
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      mb: 3,
      pb: 2,
      borderBottom: '1px solid',
      borderColor: 'divider'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            background: theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.main, 0.15)
              : alpha(theme.palette.primary.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <BreakdownIcon sx={{ color: 'primary.main', fontSize: 18 }} />
        </Box>
        <Box>
          <Typography 
            variant="h4" 
            component="h2"
            sx={{ 
              fontWeight: 700,
              letterSpacing: '-0.5px',
              fontSize: '1.75rem',
              color: theme.palette.text.primary,
              mb: 0.5
            }}
          >
            Production Breakdown
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.text.secondary,
              fontSize: '0.875rem'
            }}
          >
            Scene-by-scene production element tracking
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {(() => {
          let n = null;
          let apiMatch = selectedApiScene;
          try { if (!apiMatch && selectedScene) apiMatch = resolveApiScene(selectedScene); } catch {}
          if (apiMatch) {
            n = (apiMatch.sceneNumber && typeof apiMatch.sceneNumber === 'object')
              ? (apiMatch.sceneNumber.standard || apiMatch.sceneNumber.original || apiMatch.number)
              : (apiMatch.sceneNumber || apiMatch.number);
          }
          if (!n && selectedScene) {
            if (selectedScene.sceneNumberResolved) {
              n = selectedScene.sceneNumberResolved;
            } else if (typeof selectedScene.sceneNumber === 'object' && selectedScene.sceneNumber) {
              n = selectedScene.sceneNumber.standard || selectedScene.sceneNumber.original || null;
            } else {
              n = selectedScene.number || selectedScene.sceneNumber || null;
            }
          }
          // Final fallback: show position in list so the indicator still changes visually on selection
          if (!n && selectedScene) {
            const idx = scenes.findIndex(s => (s.id || s._id) === (selectedScene.id || selectedScene._id));
            if (idx !== -1) n = String(idx + 1);
          }
          return (
            <Typography variant="caption" color="text.secondary">
              Selected Scene #: {n ? String(n) : '-'}
            </Typography>
          );
        })()}
        <Button
          variant="contained"
          color="secondary"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={loading || !selectedScene}
          size="small"
        >
          Print Breakdown Sheet
        </Button>
      </Box>
    </Box>
    
    {/* Script Selection */}
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined" size="small" sx={textFieldSx}>
            <InputLabel id="script-select-label">Working Script</InputLabel>
            <Select
              labelId="script-select-label"
              value={selectedScript?._id || ''}
              onChange={handleScriptChange}
              label="Working Script"
              disabled={loading || scripts.length === 0}
            >
              {scripts.map((script) => (
                <MenuItem key={script._id} value={script._id}>
                  {script.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          {selectedScript && (
              <Typography variant="body2" color="text.secondary">
                Last modified: {new Date(selectedScript.updatedAt || selectedScript.createdAt).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Scene List */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={0} 
            variant="outlined" 
            sx={{ 
              height: '75vh', 
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 1,
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#000' : '#fff',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 16px rgba(0,0,0,0.3)'
                : '0 2px 8px rgba(0,0,0,0.06)'
            }}
          >
            <Box sx={{ 
              p: 2, 
              borderBottom: 1, 
              borderColor: 'divider',
              bgcolor: 'background.subtle',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Scene Navigator
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {scenes.length} scenes
              </Typography>
            </Box>
            
            <Box sx={{ p: 1.5, overflow: 'auto', flexGrow: 1 }}>
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              )}
              
              {scenes.length === 0 && !loading ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No scenes found
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<ImportIcon />}
                    onClick={handleImportFromScriptManager}
                    disabled={!selectedScript}
                  >
                    Import Scenes
                  </Button>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {scenes.map((scene, index) => {
                    // Get the original scene number from the screenplay
                    const sceneNumber = typeof scene.sceneNumber === 'object' ? 
                      (scene.sceneNumber.standard || scene.sceneNumber.scriptOrder) : 
                      (scene.sceneNumber || scene.displayIndex);
                      
                    // Format the scene number for display
                    const displayNumber = sceneNumber || (index + 1);
                      
                    // Extract INT/EXT and DAY/NIGHT if available - MOVED UP to fix reference error
                    let location = '';
                    let timeOfDay = '';
                    
                    // Try to get the best possible scene heading
                    let sceneHeading = 'Untitled Scene';
                    
                    // Debug what we have available
                    console.log(`Scene ${index} data:`, {
                      id: scene.id || scene._id,
                      heading: scene.heading,
                      text: scene.text,
                      hasContent: !!scene.content,
                      contentLength: scene.content ? scene.content.length : 0,
                      startLine: scene.startLine,
                      endLine: scene.endLine
                    });
                    
                    // Try multiple sources for the heading in order of preference
                    if (scene.heading && typeof scene.heading === 'string' && scene.heading !== 'Untitled Scene') {
                      sceneHeading = scene.heading;
                    } else if (scene.slugline && typeof scene.slugline === 'string') {
                      sceneHeading = scene.slugline;
                    } else if (scene.text && typeof scene.text === 'string') {
                      sceneHeading = scene.text;
                    } else if (scene.content && typeof scene.content === 'string') {
                      // Try to extract a slugline from the content
                      const lines = scene.content.split('\n');
                      for (let i = 0; i < Math.min(5, lines.length); i++) {
                        const line = lines[i].trim();
                        if (line && (line.includes('INT.') || line.includes('EXT.'))) {
                          sceneHeading = line;
                          break;
                        }
                      }
                    }
                    
                    // If we still don't have a heading, create one with location info if available
                    if (sceneHeading === 'Untitled Scene' && (scene.location || scene.setting)) {
                      const sceneLocation = scene.location || scene.setting;
                      sceneHeading = `${location || 'INT.'} ${sceneLocation} - ${timeOfDay || 'DAY'}`;
                    }
                    
                    if (sceneHeading) {
                      if (sceneHeading.includes('INT.')) {
                        location = 'INT.';
                      } else if (sceneHeading.includes('EXT.')) {
                        location = 'EXT.';
                      } else if (sceneHeading.includes('INT/EXT.')) {
                        location = 'INT/EXT.';
                      }
                      
                      if (sceneHeading.includes('DAY')) {
                        timeOfDay = 'DAY';
                      } else if (sceneHeading.includes('NIGHT')) {
                        timeOfDay = 'NIGHT';
                      } else if (sceneHeading.includes('EVENING')) {
                        timeOfDay = 'EVENING';
                      } else if (sceneHeading.includes('MORNING')) {
                        timeOfDay = 'MORNING';
                      }
                    }
                    
                    // Extract first action line from scene content
                    let actionLine = '';
                    if (scene.content && typeof scene.content === 'string') {
                      // Split content by lines and find first non-heading, non-empty line
                      const lines = scene.content.split('\n');
                      for (let i = 0; i < lines.length; i++) {
                        const line = lines[i].trim();
                        // Skip empty lines, headings, and character names
                        if (line && 
                            !line.startsWith('INT.') && 
                            !line.startsWith('EXT.') && 
                            !line.match(/^[A-Z\s]+$/) && // Skip all caps lines (character names)
                            !line.includes('CUT TO:')) {
                          actionLine = line;
                          break;
                        }
                      }
                    } else if (scene.text && typeof scene.text === 'string' && scene.text !== sceneHeading) {
                      // If scene has text that's not the heading, use that
                      actionLine = scene.text;
                    }
                    
                    return (
                      <Box
                        key={scene.id || index}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          border: '1px solid',
                          borderColor: selectedScene && selectedScene.id === scene.id ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          backgroundColor: (theme) => {
                            const isDark = theme.palette.mode === 'dark';
                            const base = isDark ? '#000' : '#fff';
                            const highlight = isDark ? '#1e2124' : '#f5f5f7';
                            return (selectedScene && selectedScene.id === scene.id) ? highlight : base;
                          },
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1e2124' : '#f5f5f7',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 3px 5px rgba(0,0,0,0.05)'
                          }
                        }}
                        onClick={() => handleSceneSelect(scene)}
                      >
                        {/* Scene number and INT/EXT info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {/* Show only the original screenplay scene number */}
                            Scene {displayNumber}
                          </Typography>
                          {location && timeOfDay && (
                            <Typography variant="caption" sx={{ 
                              color: timeOfDay === 'DAY' ? 'info.main' : 'text.secondary',
                              fontWeight: 500
                            }}>
                              {location} {timeOfDay}
                            </Typography>
                          )}
                        </Box>
                        
                        {/* Slugline - prominently displayed */}
                        <Typography 
                          variant="body2" 
                          sx={{
                            fontWeight: 500,
                            color: 'text.primary',
                            backgroundColor: alpha('#000', 0.03),
                            p: 0.75,
                            borderRadius: 1,
                            mb: 0.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            fontSize: '0.85rem',
                            textTransform: 'uppercase'
                          }}
                        >
                          {sceneHeading}
                        </Typography>
                        {actionLine && (
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ 
                              display: 'block', 
                              mt: 0.5,
                              fontStyle: 'italic',
                              maxHeight: '2.4em',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {actionLine}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </List>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Breakdown Items */}
        <Grid item xs={12} md={8}>
          <Paper 
            elevation={0} 
            variant="outlined" 
            sx={{ 
              height: '75vh', 
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 1,
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#000' : '#fff',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 16px rgba(0,0,0,0.3)'
                : '0 2px 8px rgba(0,0,0,0.06)'
            }}
          >
            <Box sx={{ 
              p: 2, 
              borderBottom: 1, 
              borderColor: 'divider',
              bgcolor: 'background.subtle',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              {selectedScene ? (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {/* Get scene number from multiple possible sources */}
                    {(() => {
                      // Try to get the best scene number
                      let sceneNum;
                      
                      // First check for scene number in the object
                      if (typeof selectedScene.sceneNumber === 'object') {
                        sceneNum = selectedScene.sceneNumber.standard || selectedScene.sceneNumber.scriptOrder;
                      } 
                      // Then check for direct scene number
                      else if (selectedScene.sceneNumber !== undefined) {
                        sceneNum = selectedScene.sceneNumber;
                      }
                      // Check for display index
                      else if (selectedScene.displayIndex !== undefined) {
                        sceneNum = selectedScene.displayIndex;
                      }
                      // For scenes extracted directly from script, use line number
                      else if (selectedScene.id && selectedScene.id.startsWith('script_scene_')) {
                        // Extract scene number from ID (e.g., script_scene_123)
                        const sceneIndex = parseInt(selectedScene.id.replace('script_scene_', ''));
                        // Find position in scenes array
                        const scenePosition = scenes.findIndex(s => s.id === selectedScene.id);
                        sceneNum = scenePosition !== -1 ? scenePosition + 1 : sceneIndex + 1;
                      }
                      
                      return `Scene ${sceneNum || selectedScene.startLine || '1'}`;
                    })()} 
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sceneNameOverrides[(selectedScene.id || selectedScene._id) || ''] || ''}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <TextField
                      size="small"
                      variant="outlined"
                      label="Scene Name"
                      placeholder="Enter scene name"
                      value={sceneNameOverrides[(selectedScene.id || selectedScene._id) || ''] || ''}
                      onChange={(e) => setSceneNameOverride((selectedScene.id || selectedScene._id) || '', e.target.value)}
                      inputProps={{ maxLength: 80 }}
                      helperText={`${(sceneNameOverrides[(selectedScene.id || selectedScene._id) || ''] || '').length}/80`}
                      FormHelperTextProps={{ sx: { m: 0, mt: 0.5, fontSize: '0.72rem', color: 'text.disabled' } }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 320, flex: '0 0 auto', ...textFieldSx }}
                    />
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Description"
                      placeholder="Add a concise description"
                      value={sceneDescriptionOverrides[(selectedScene.id || selectedScene._id) || ''] || ''}
                      onChange={(e) => setSceneDescriptionOverride((selectedScene.id || selectedScene._id) || '', e.target.value)}
                      inputProps={{ maxLength: 400 }}
                      helperText={`${(sceneDescriptionOverrides[(selectedScene.id || selectedScene._id) || ''] || '').length}/400`}
                      FormHelperTextProps={{ sx: { m: 0, mt: 0.5, fontSize: '0.72rem', color: 'text.disabled' } }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flexGrow: 1, minWidth: 240, ...textFieldSx }}
                    />
                  </Box>
                </Box>
              ) : (
                <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                  Select a scene to view breakdown elements
                </Typography>
              )}
              
              <Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddItem}
                  disabled={!selectedScene}
                  sx={{ mr: 1 }}
                >
                  Add Element
                </Button>
              </Box>
            </Box>

            <Box sx={{ p: 0, overflow: 'auto', flexGrow: 1 }}>
              {!selectedScene ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    Please select a scene from the navigator to view its breakdown elements
                  </Typography>
                </Box>
              ) : loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <Box sx={{ p: 2 }}>
                  {Object.keys(breakdownItems).length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        No breakdown elements found for this scene
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<RefreshIcon />}
                        onClick={autoTagScript}
                        disabled={loading}
                      >
                        Auto-Tag Elements
                      </Button>
                    </Box>
                  ) : (
                    <Grid container spacing={3}>
                      {Object.entries(breakdownItems).map(([category, items]) => (
                        <Grid item xs={12} key={category}>
                          <Paper 
                            variant="outlined" 
                            sx={{ 
                              overflow: 'hidden',
                              borderRadius: 1,
                              borderColor: categoryColors[category] || '#ccc',
                              borderWidth: 2,
                              boxShadow: theme.palette.mode === 'dark'
                                ? '0 2px 8px rgba(0,0,0,0.2)'
                                : '0 1px 4px rgba(0,0,0,0.04)'
                            }}
                          >
                            <Box sx={{ 
                              px: 2, 
                              py: 1.5,
                              borderBottom: 1,
                              borderColor: 'divider',
                              bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1e2124' : '#f5f5f7',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  fontWeight: 600,
                                  color: 'text.primary',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <Box 
                                  component="span" 
                                  sx={{ 
                                    width: 12, 
                                    height: 12, 
                                    borderRadius: '50%', 
                                    bgcolor: TAG_TYPES[category]?.color || categoryColors[category] || 'divider',
                                    mr: 1,
                                    display: 'inline-block'
                                  }} 
                                />
                                {TAG_TYPES[category]?.label || categoryLabels[category] || category}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {items.length} {items.length === 1 ? 'item' : 'items'}
                              </Typography>
                            </Box>
                            
                            <TableContainer sx={{ maxHeight: 300, borderRadius: 1 }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Element</TableCell>
                                    <TableCell width={80}>Line</TableCell>
                                    <TableCell>Keywords</TableCell>
                                    <TableCell align="right" width={100}>Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {items.map((item) => (
                                    <TableRow key={item.id} hover>
                                      <TableCell sx={{ fontWeight: 500 }}>{item.text}</TableCell>
                                      <TableCell>{item.lineNumber}</TableCell>
                                      <TableCell>
                                        {item.keyword?.split(',').map((keyword, idx) => (
                                          <Chip 
                                            key={idx} 
                                            label={keyword.trim()} 
                                            size="small" 
                                            variant="outlined"
                                            sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }}
                                          />
                                        ))}
                                      </TableCell>
                                      <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleEditItem(item)} sx={{ mr: 0.5 }}>
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDeleteItem(item)} color="error">
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 8px 32px rgba(0, 0, 0, 0.5)' 
              : '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle>
          {editItem && editItem.id.includes('manual_') ? 'Add Breakdown Item' : 'Edit Breakdown Item'}
        </DialogTitle>
        <DialogContent>
          {editItem && (
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Text"
                fullWidth
                value={editItem.text}
                onChange={(e) => setEditItem({...editItem, text: e.target.value})}
                margin="normal"
                sx={textFieldSx}
              />
              <TextField
                select
                label="Category"
                fullWidth
                value={editItem.type}
                onChange={(e) => setEditItem({
                  ...editItem, 
                  type: e.target.value,
                  color: TAG_TYPES[e.target.value]?.color || categoryColors[e.target.value] || '#000000'
                })}
                margin="normal"
                sx={textFieldSx}
              >
                {/* Use TAG_TYPES from SimpleScreenplayViewer for consistency */}
                {Object.entries(TAG_TYPES).map(([value, tagInfo]) => (
                  <MenuItem key={value} value={value}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        component="span" 
                        sx={{ 
                          width: 12, 
                          height: 12, 
                          borderRadius: '50%', 
                          bgcolor: tagInfo.color,
                          mr: 1,
                          display: 'inline-block'
                        }} 
                      />
                      {tagInfo.label}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Line Number"
                type="number"
                fullWidth
                value={editItem.lineNumber}
                onChange={(e) => setEditItem({...editItem, lineNumber: parseInt(e.target.value) || 0})}
                margin="normal"
                sx={textFieldSx}
              />
              <TextField
                label="Keywords"
                fullWidth
                value={editItem.keyword}
                onChange={(e) => setEditItem({...editItem, keyword: e.target.value})}
                margin="normal"
                sx={textFieldSx}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleSaveItem} 
            color="primary" 
            variant="contained"
            startIcon={loading ? <CircularProgress size={24} /> : <SaveIcon />}
            disabled={loading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({...notification, open: false})}
      >
        <Alert 
          onClose={() => setNotification({...notification, open: false})} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      
      {/* Script Manager Dialog */}
      <Dialog 
        open={scriptManagerOpen} 
        onClose={() => setScriptManagerOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 8px 32px rgba(0, 0, 0, 0.5)' 
              : '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Script Manager</Typography>
            <IconButton onClick={() => setScriptManagerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <ScriptManager 
            projectId={projectId} 
            onScriptSelect={handleScriptSelected}
          />
        </DialogContent>
      </Dialog>
      
      {/* Import Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 8px 32px rgba(0, 0, 0, 0.5)' 
              : '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle>Import Scenes from Script</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2 }}>
            This will import all scenes from the selected script: <strong>{selectedScript?.title}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Any existing breakdown items will be preserved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleImportConfirm} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Breakdown;
